import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TableHeaderCell from "./TableHeaderCell";

interface ColumnConfig<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  align?: "left" | "center" | "right";
  width?: number | string;

  //Filter
  filter?: {
    value: string;
    options: string[];
    onChange: (value: any) => void;
  };
}

interface DataTableProps<T> {
  columns: ColumnConfig<T>[];
  data: T[];
  selectedRows?: string[];
  onRowClick?: (row: T) => void;
  onSelectRow?: (id: string) => void;
  showCheckbox?: boolean;
  getRowId: (row: T) => string;
  actionRenderer?: (row: T) => React.ReactNode;
  emptyMessage?: string;
  // Sorting
  sortField?: string;
  sortOrder?: "asc" | "desc" | null;
  onSort?: (columnKey: string) => void;
}

function DataTable<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  // getRowId,
  actionRenderer,
  emptyMessage,
  sortOrder,
  onSort,
}: DataTableProps<T>) {
  // console.log('DataTable data', data);
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table
        sx={{
          "& th, & td": {
            textAlign: "center", // <-- Center everything
            borderRight: "1px solid #e0e0e0",
          },
          "& th:last-child, & td:last-child": { borderRight: "none" },
        }}
      >
        <TableHead>
          <TableRow sx={{ borderBottom: 1, borderColor: "grey.200" }}>
            {columns.map((col) => (
              <TableCell
                key={String(col.key)}
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "grey.700",
                  whiteSpace: "nowrap",
                  position: "relative",
                  textAlign: "center",
                }}
              >
                <TableHeaderCell
                  label={col.label}
                  columnKey={String(col.key)}
                  sortable={col.sortable}
                  sortOrder={sortOrder === col.key ? sortOrder : null}
                  onSort={onSort}
                  filter={col.filter}
                />
              </TableCell>
            ))}
            {actionRenderer && (
              <TableCell
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "grey.700",
                  textAlign: "center",
                }}
              >
                Action
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + (actionRenderer ? 1 : 0)}
                align="center"
                sx={{ py: 4 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, i) => {
              // const id = getRowId(row);
              // console.log('row', row);
              
              return (
                <TableRow
                  key={i}
                  onClick={() => onRowClick?.(row)}
                  hover
                  sx={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={String(col.key)}
                      align={col.align || "left"}
                      sx={{ fontSize: "0.875rem", color: "grey.900" }}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </TableCell>
                  ))}

                  {actionRenderer && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {actionRenderer(row)}
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default DataTable;
